import { getFormContext } from "@/config/FormContext"
import { InputCostumeErrorType, InputErrorType } from "@/types/form-types"
import { validateInput } from "@/utilities/input-utilities"
import { useContext, useEffect, useRef, useState } from "react"
import InputWrapper from "./input-common/InputWrapper"
import { useTranslation } from "react-i18next"
import { getTranslationText } from "@/utilities/translation-utilities"
import '../Form.scss'

type ColorInputPropsType = {
    customErrors?: InputCostumeErrorType<string, HTMLInputElement>[]
    name?: string,
    id?: string
    autofill?: string
    label?: string | React.ReactNode
    required?: boolean
    autoFocus?: boolean
    value?: string | null,
    touched?: boolean,
    readonly?: boolean,
    disabled?: boolean,
    placeholder?: string,
    hideErrorMessages?: boolean,
    description?: string | React.ReactNode,
    wrapperClassName?: string,
    onChange?: (s: string | undefined, input: React.ChangeEvent<HTMLInputElement>) => void
    onBlur?: (s: string | undefined, input: React.ChangeEvent<HTMLInputElement>) => void,
    onFocus?: (input: React.ChangeEvent<HTMLInputElement>) => void
}

const ColorInput = (props: ColorInputPropsType) => {
    const formContext = getFormContext(useContext);
    const [t] = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const inputWrapper = useRef<HTMLDivElement>(null);
    let [touched, setTouched] = useState(props.touched || formContext.submitted ? true : false);
    let [errors, setErrors] = useState<InputErrorType[]>([]);
    let [isFocused, setIsFocused] = useState<boolean>(false);
    let colorInputId = props.id || `color-input-${Math.random().toString(16).slice(2)}`;

    const placeholder = getTranslationText(props.placeholder, t);

    useEffect(() => {
        validate(true);
        if (props.autoFocus && input.current) {
            input.current.focus();
        }
    }, []);

    useEffect(() => {
        setTouched(formContext.submitted);
        validate(!formContext.submitted);
    }, [formContext.submitted])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof props.onChange == 'function') {
            props.onChange(e.currentTarget.value, e);
        }

        if (touched) {
            validate();
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        validate();
        if (typeof props.onBlur == 'function') {
            props.onBlur(e.currentTarget.value, e);
        }
        setIsFocused(false);
    }

    function validate(soft?: boolean) {
        setErrors(validateInput({
            value: props.value,
            input: input?.current || undefined,
            inputWrapper: inputWrapper?.current || undefined,
            customErrors: props.customErrors,
            soft: soft ? true : false
        }) || []);
    }

    const colorPreview = {
        backgroundColor: props.value || '#000000',
        width: '2rem',
        height: '2rem',
        borderRadius: '0.25rem',
        border: '1px solid #ccc',
        flexShrink: 0,
        margin: '0 0.5rem'
    };

    return (
        <InputWrapper
            errors={errors}
            inputWrapperRef={inputWrapper}
            label={props.label}
            wrapperClassName={props.wrapperClassName}
            required={props.required}
            htmlFor={colorInputId}
            hideErrorMessages={props.hideErrorMessages}
        >
            <div style={colorPreview} />
            <input
                type="color"
                autoComplete="none"
                id={colorInputId}
                disabled={props.disabled}
                readOnly={props.readonly}
                value={props.value === null ? undefined : props.value}
                onFocus={v => {
                    setIsFocused(true);
                    if (props.onFocus) props.onFocus(v);
                }}
                onChange={onChange}
                onBlur={e => onBlur(e)}
                placeholder={placeholder || ''}
                style={{ width: "0px", height: "0px", padding: 0, minWidth: 0, border: 'none' }}
            />
            <input
                ref={input}
                type="text"
                name={props.name}
                required={props.required}
                id={props.id ? `${props.id}-text` : undefined}
                autoComplete="none"
                disabled={props.disabled}
                readOnly={props.readonly}
                value={props.value === null ? undefined : props.value}
                onFocus={v => {
                    setIsFocused(true);
                    if (props.onFocus) props.onFocus(v);
                }}
                onChange={onChange}
                onBlur={e => onBlur(e)}
                placeholder={placeholder || ''}
            />
        </InputWrapper>
    );
}

export default ColorInput;